import { apiClient } from "./apiClient";
import {
  IReportPayload,
  IReportData,
  EvaluatorType,
  evaluatorTypeLabel,
} from "../features/reports/interfaces/IReport";

/**
 * Busca os dados brutos do relatório no backend
 */
export async function getReportPayload(
  evaluationId: string
): Promise<IReportPayload> {
  const { data } = await apiClient.get<IReportPayload>(
    `/reports/${evaluationId}`
  );
  return data;
}

/**
 * Transforma o payload do backend em dados processados para o frontend
 */
export function processReportPayload(payload: IReportPayload): IReportData {
  if (!payload.evaluation || payload.evaluation.length === 0) {
    throw new Error("Nenhuma avaliação encontrada no payload");
  }

  const evaluation = payload.evaluation[0];

  // Processa e agrupa avaliadores
  const evaluators = payload.evaluators.map((ev) => {
    const evaluatorType = getEvaluatorTypeFromChoice(ev.evaluatorTypeChoice);
    return {
      id: ev.id,
      evaluatorType,
      evaluatorTypeLabel:
        evaluatorTypeLabel[evaluatorType] || ev.evaluatorTypeChoice,
      statusCode: ev.statusCode,
      name: ev.evaluatorEmployeeName || ev.clientName || "Desconhecido",
      clientName: ev.clientName,
    };
  });

  // Agrupa avaliadores por tipo
  const evaluatorsByType = {
    selfEvaluation: evaluators.find(
      (e) => e.evaluatorType === EvaluatorType.AutoEvaluation
    ),
    teamLeaders: evaluators.filter(
      (e) => e.evaluatorType === EvaluatorType.TeamLeader
    ),
    managers: evaluators.filter(
      (e) => e.evaluatorType === EvaluatorType.Manager
    ),
    colleagues: evaluators.filter(
      (e) => e.evaluatorType === EvaluatorType.Colleague
    ),
    friends: evaluators.filter((e) => e.evaluatorType === EvaluatorType.Friend),
    clients: evaluators.filter((e) => e.evaluatorType === EvaluatorType.Client),
  };

  // Calcula resumo
  const totalQuestions = payload.allAnswers.reduce(
    (sum, cat) => sum + cat.questions.length,
    0
  );

  // Mapeia as perguntas para garantir que o questionType seja consistente
  const categories = payload.allAnswers.map((cat) => ({
    ...cat,
    questions: cat.questions.map((q: any) => ({
      ...q,
      questionType: q.questionTypeChoice || q.questionType, // Usa questionTypeChoice se disponível
    })),
  }));

  return {
    evaluationId: evaluation.evaluationId,
    evaluationYear: evaluation.year,
    evaluationEndDate: evaluation.endDate,
    evaluationStatus: evaluation.status,
    hasReport: evaluation.hasReport === true,
    canHrComplete: payload.canHrComplete,
    hrComment: evaluation.comment,
    finalEvaluation: evaluation.finalEvaluation,
    evaluatorsByType,
    categories,
    summary: {
      totalEvaluators: evaluators.length,
      totalCategories: categories.length,
      totalQuestions,
    },
  };
}

/**
 * Converte o tipo de avaliador (string do backend) para enum numérico
 */
function getEvaluatorTypeFromChoice(choice: string): number {
  const normalized = (choice || "").toLowerCase().trim();

  // Handle numeric choices coming as strings
  if (/^\d+$/.test(normalized)) {
    const numeric = Number(normalized);
    if (numeric in EvaluatorType) return numeric as EvaluatorType;
  }

  switch (normalized) {
    case "colaborador":
    case "self":
      return EvaluatorType.AutoEvaluation;
    case "team leader":
      return EvaluatorType.TeamLeader;
    case "management":
      return EvaluatorType.Manager;
    case "colleague":
    case "colega trabalho":
      return EvaluatorType.Colleague;
    case "friend":
    case "colega social":
      return EvaluatorType.Friend;
    case "client":
    case "cliente":
      return EvaluatorType.Client;
    default:
      console.warn(`Tipo de avaliador desconhecido: ${choice}`);
      return EvaluatorType.Colleague;
  }
}

/**
 * Função combinada: busca e processa em uma chamada
 */
export async function getReportData(
  evaluationId: string
): Promise<IReportData> {
  const payload = await getReportPayload(evaluationId);
  return processReportPayload(payload);
}
